/**
 * The Reach Wire panel (M4 spec §15 item 2): filter buttons (All, This system, Nearby, My cargo, Disabled hulls) and the items,
 * newest first, with day stamps, the system's name (never its id, H6), severity and cause (dropped when the headline says it). Clicking an item asks to focus the galaxy map on its system.
 * Plain buttons in DOM order, so M2's station screen focus model, the keyboard and the pad (`navigateButtons`)
 * all reach them. In the dashboard (Intel › Wire, `createWireSection`; W3 card M1) the shell's focus inputs drive the
 * same buttons (`useButtonNav`), and left/right step the filter.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { WireItem } from '../../strategic/types'
import type { DashNavPort } from '../dashboard/focus'
import { dashboardOpen, type DashSection, type DashSectionProps } from '../dashboard/registry'
import { useButtonNav } from './dashNav'
import { stepFilter, WIRE_FILTER_LABELS, WIRE_FILTERS, wirePanelModel, type WireFilter, type WireFilterContext } from './wireModel'
import './strategic.css'

export interface WirePanelProps {
  items: readonly WireItem[]
  context: WireFilterContext
  initialFilter?: WireFilter
  /** An item was chosen: focus the galaxy map on its system. */
  onSelect?: (systemId: string) => void
  /** The dashboard's focus inputs (Intel › Wire); a station tab has none. */
  nav?: DashNavPort
}

export function WirePanel(p: WirePanelProps): ReactNode {
  const [filter, setFilter] = useState<WireFilter>(p.initialFilter ?? 'all')
  const root = useRef<HTMLDivElement>(null)
  useButtonNav(p.nav, root, (dir) => setFilter((f) => stepFilter(f, dir)))
  const model = wirePanelModel(p.items, filter, p.context)
  return (
    <div
      ref={root}
      className="sui sui-wire"
      data-testid="wire-panel"
      onKeyDown={(e) => {
        if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
          e.preventDefault()
          setFilter((f) => stepFilter(f, e.code === 'ArrowLeft' ? -1 : 1))
        }
      }}
    >
      <header className="sui-head">
        <h2 className="sui-title">Reach Wire</h2>
      </header>
      <div className="sui-filters" role="tablist" aria-label="Wire filter">
        {WIRE_FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={f === filter}
            className="sui-btn sui-filter"
            data-active={f === filter ? '' : undefined}
            data-testid={`wire-filter-${f}`}
            onClick={() => setFilter(f)}
          >
            {f === filter ? '▸ ' : ''}
            {WIRE_FILTER_LABELS[f]}
          </button>
        ))}
      </div>
      {model.empty !== null ? <p className="sui-empty">{model.empty}</p> : null}
      <ol className="sui-list">
        {model.rows.map((r) => (
          <li key={r.id}>
            <button type="button" className="sui-btn sui-item" data-severity={r.severity} data-testid="wire-item" onClick={() => p.onSelect?.(r.systemId)}>
              <span className="sui-item__meta">
                <span className="sui-sev" aria-label={r.severityLabel}>
                  {r.severityGlyph} {r.severityLabel}
                </span>{' '}
                {'·'} {r.dayLabel}
                {r.system !== null ? <span className="sui-item__system"> {'·'} {r.system}</span> : null}
                {r.outlet !== null ? <span className="sui-item__outlet" data-testid="wire-outlet"> {'·'} {r.outlet}</span> : null}
              </span>
              <span className="sui-item__headline">{r.headline}</span>
              {r.cause !== null ? <span className="sui-item__cause">Cause: {r.cause}</span> : null}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Intel › Wire (the dashboard section)
// ---------------------------------------------------------------------------------------------

export interface WireSectionConfig {
  readonly label: string
  readonly order: number
  /** Items at or above this severity count toward the unread badge. */
  readonly badgeMinSeverity: number
}

export interface WireSectionDeps {
  /** The Wire now (`StrategicClient.wire`). */
  items(): readonly WireItem[]
  context(): WireFilterContext
  /** New Wire items (`StrategicClient.onWire`); returns the stop. */
  onWire(fn: (item: WireItem) => void): () => void
  /** An item was chosen (the dashboard has closed): focus the galaxy map on its system. */
  onSelect(systemId: string): void
  readonly config: WireSectionConfig
  /** True while the dashboard shows (default: the registry's host). */
  isOpen?: () => boolean
}

export interface WireSection {
  readonly section: DashSection
  /** The section was viewed: the unread count drops to 0. */
  markSeen(): void
  dispose(): void
}

/** Intel › Wire (`intel.wire`, deep link `wirePanel` = N; dashboard spec §2.2 tab 6). */
export function createWireSection(deps: WireSectionDeps): WireSection {
  const isOpen = deps.isOpen ?? dashboardOpen
  let unread = 0
  const stop = deps.onWire((item) => {
    if (item.severity >= deps.config.badgeMinSeverity) unread++
  })
  const markSeen = (): void => {
    unread = 0
  }

  function WireSectionBody(p: DashSectionProps): ReactNode {
    const [, setTick] = useState(0)
    // New items re-render while the dashboard shows; closed, the reopening render reads the Wire afresh.
    useEffect(
      () =>
        deps.onWire(() => {
          if (isOpen()) setTick((n) => n + 1)
        }),
      [],
    )
    useEffect(() => {
      if (isOpen()) markSeen()
    })
    return (
      <WirePanel
        items={deps.items()}
        context={deps.context()}
        nav={p.nav}
        onSelect={(systemId) => {
          p.port.close(false)
          deps.onSelect(systemId)
        }}
      />
    )
  }

  return {
    section: {
      id: 'intel.wire',
      tab: 'intel',
      order: deps.config.order,
      label: deps.config.label,
      deepLink: 'wirePanel',
      available: () => true,
      badge: () => unread,
      component: WireSectionBody,
    },
    markSeen,
    dispose: stop,
  }
}
